import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { InvoiceFileLink } from "./InvoiceFileLink";
import { Button } from "@/components/ui/button";
import { PackageCheck } from "lucide-react";
import { fmtDate, fmtDateTime, fmtQty, STATUS_STYLES } from "./mcfUtils";

const Field = ({ label, children, testId }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="text-sm font-medium mt-0.5" data-testid={testId}>{children || "—"}</p>
  </div>
);

const Kpi = ({ label, value, tone, testId }) => (
  <div className="rounded-lg border bg-white px-4 py-3">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-2xl font-semibold ${tone}`} data-testid={testId}>{fmtQty(value)}</p>
  </div>
);

const fmtChange = (k, v) => {
  if (Array.isArray(v)) return `${k}: ${v[0] ?? "—"} → ${v[1] ?? "—"}`;
  if (v && typeof v === "object") return Object.entries(v).map(([sku, x]) => fmtChange(`${k} ${sku}`, x)).join(" · ");
  return `${k}: ${v}`;
};
const describe = (c) => Object.entries(c || {}).map(([k, v]) => fmtChange(k, v)).filter(Boolean).join(" · ");

export function TransferViewDialog({ transfer, onClose, onUpdateGrn }) {
  if (!transfer) return null;
  const t = transfer;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="mcf-transfer-view-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            Stock Transfer · {t.invoice_number}
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[t.status]}`} data-testid="mcf-view-status">{t.status}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-lg bg-slate-50 px-3 py-2"><InvoiceFileLink file={t.invoice_file} testId="mcf-view-invoice-file" /></div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Total Sent Units" value={t.sent_units} tone="text-slate-900" testId="mcf-view-sent-units" />
          <Kpi label="Total GRN Units" value={t.grn_units} tone="text-[#1F8F88]" testId="mcf-view-grn-units" />
          <Kpi label="Pending GRN Units" value={t.pending_grn_units} tone={t.pending_grn_units > 0 ? "text-amber-700" : "text-emerald-700"} testId="mcf-view-pending-units" />
          <Kpi label="Excess GRN Units" value={t.excess_grn_units} tone={t.excess_grn_units > 0 ? "text-rose-700" : "text-slate-400"} testId="mcf-view-excess-units" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 rounded-lg border p-4">
          <Field label="Invoice No." testId="mcf-view-invoice-number">{t.invoice_number}</Field>
          <Field label="Invoice Date">{fmtDate(t.invoice_date)}</Field>
          <Field label="Pickup Date" testId="mcf-view-pickup-date">{fmtDate(t.pickup_date)}</Field>
          <Field label="Courier Partner">{t.courier_partner}</Field>
          <Field label="Pickup Location" testId="mcf-view-pickup">{t.pickup_warehouse_code ? `${t.pickup_location} (${t.pickup_warehouse_code})` : t.pickup_location}{t.pickup_address && <span className="block text-xs font-normal text-muted-foreground">{t.pickup_address}</span>}</Field>
          <Field label="Drop Location" testId="mcf-view-drop">{`${t.drop_location_name} (${t.drop_mcf_code})`}</Field>
          <Field label="Drop City">{t.drop_city}</Field>
          <Field label="Drop State">{t.drop_state}</Field>
          <Field label="Created">{`${fmtDateTime(t.created_at)} · ${t.created_by}`}</Field>
          <Field label="Last Updated">{`${fmtDateTime(t.updated_at)} · ${t.updated_by}`}</Field>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">SKU-wise Details</h3>
            {onUpdateGrn && <Button size="sm" variant="outline" className="h-8 border-[#1F8F88] text-[#1F8F88] hover:bg-teal-50 hover:text-[#1F8F88]" onClick={onUpdateGrn} data-testid="mcf-view-update-grn"><PackageCheck className="h-4 w-4 mr-1" />Update GRN</Button>}
          </div>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>SKU ID</TableHead><TableHead>SKU Name</TableHead><TableHead>Item Code</TableHead>
                  <TableHead className="text-right">Sent</TableHead><TableHead className="text-right">GRN</TableHead><TableHead className="text-right">Pending</TableHead><TableHead className="text-right">Excess</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {t.items.map((i) => (
                  <TableRow key={i.line_id} data-testid={`mcf-view-item-${i.sku_id}`}>
                    <TableCell className="font-mono text-xs">{i.sku_id}</TableCell>
                    <TableCell>{i.sku_name}</TableCell>
                    <TableCell className="font-mono text-xs">{i.item_code || "—"}</TableCell>
                    <TableCell className="text-right">{fmtQty(i.sent_qty)}</TableCell>
                    <TableCell className="text-right">{fmtQty(i.grn_qty)}{i.discrepancy_approved && <span className="block text-[10px] text-rose-700">approved excess</span>}</TableCell>
                    <TableCell className={`text-right font-medium ${i.pending_grn_qty > 0 ? "text-amber-700" : "text-emerald-700"}`}>{fmtQty(i.pending_grn_qty)}</TableCell>
                    <TableCell className={`text-right font-medium ${i.excess_grn_qty > 0 ? "text-rose-700" : "text-slate-400"}`}>{fmtQty(i.excess_grn_qty)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        <div>
          <h3 className="font-semibold mb-2">Audit History</h3>
          <ol className="space-y-2 border-l-2 border-teal-200 pl-4" data-testid="mcf-view-audit">
            {[...(t.audit_log || [])].reverse().map((a, n) => (
              <li key={n} className="text-sm">
                <p className="font-medium"><span className="capitalize">{a.action.replace("_", " ")}</span> <span className="font-normal text-muted-foreground">· {fmtDateTime(a.at)} · {a.by}</span></p>
                <p className="text-xs text-muted-foreground">{describe(a.changes)}</p>
              </li>
            ))}
          </ol>
        </div>
      </DialogContent>
    </Dialog>
  );
}
